import { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Backdrop,
  IconButton,
  Grid,
  Typography,
  TextField,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import { globalVariables } from "../../../global/GlobalVariables";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { useConfirm } from "material-ui-confirm";
import {
  BCPSquarePlus,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import { postData } from "../../../components/utilities/UtilityFunctions";

export function ApprovalModal(props) {
  const { organization, approvalStatus, dirty, open, setOpen, handleUpdates } =
    props;
  const globalVars = globalVariables();
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [masterApprovalReasons, setMasterApprovalReasons] = useState(
    globalVars?.approvalReasons
  );
  const [approvalReasons, setApprovalReasons] = useState(
    organization?.approvalReasons?.length ? organization.approvalReasons : [{}]
  );
  const [approvalReason, setApprovalReason] = useState(
    organization.approvalReason || ""
  );

  const updateOrganization = async (e) => {
    if (dirty) {
      postNotification(
        "info",
        `Please save the current record before ${
          approvalStatus === "approve"
            ? "approving"
            : approvalStatus === "pending"
            ? "marking as pending"
            : approvalStatus === "rejected"
            ? "rejecting"
            : "updating"
        }`
      );
      return;
    }
    var body = {
      approverId: authContext?.authState?.identity?.userId,
      approvalStatus: approvalStatus,
    };
    if (globalVars?.approvalReasons?.length > 0) {
      body.approvalReasons = [];
      body.approvalReason = "";
      if (approvalReasons?.length) {
        for (let reason of approvalReasons) {
          if (reason?.text || reason?.label) {
            body.approvalReasons.push(reason);
            body.approvalReason = `${body.approvalReason}${
              body.approvalReason !== "" ? "\n\n" : ""
            }${reason?.text || reason?.label}`;
          }
        }
      }
    } else {
      body.approvalReason = approvalReason;
    }
    try {
      setLoading(true);
      var updates = await postData(`/gamestores/${organization.id}/approval`, {
        body: body,
      });
      postNotification("success", `Organization ${approvalStatus}`);
      if (handleUpdates) {
        handleUpdates(updates);
      }
      setOpen(false);
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mars = globalVars?.approvalReasons;
    if (mars?.length) {
      if (organization?.approvalReasons?.length) {
        for (let oars of organization.approvalReasons) {
          if (typeof oars === "string") {
            mars.push({ label: oars });
          } else if (oars?.label) {
            if (!mars.filter((el) => el.label === oars.label).length) {
              mars.push(oars);
            }
          }
        }
      }
      setMasterApprovalReasons(mars);
    }
    // eslint-disable-next-line
  }, [organization]);

  return (
    <>
      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {organization?.approvalStatus === approvalStatus
                  ? approvalStatus === "approve"
                    ? "Approved Organization"
                    : approvalStatus === "pending"
                    ? "Pending Organization"
                    : approvalStatus === "rejected"
                    ? "Rejected Organization"
                    : "Organization"
                  : approvalStatus === "approve"
                  ? "Approve Organization"
                  : approvalStatus === "pending"
                  ? "Set Organization as Pending"
                  : approvalStatus === "rejected"
                  ? "Reject Organization"
                  : "Organization"}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            {masterApprovalReasons?.length > 0 ? (
              <>
                {approvalReasons?.map((approvalReason, index) => {
                  return (
                    <Grid key={index} item xs={12}>
                      <GenericPicker
                        name={index}
                        label="Reason"
                        value={approvalReason?.label}
                        inclAny={false}
                        handleChange={(e) => {
                          let reasons = approvalReasons;
                          reasons[index] = masterApprovalReasons.filter(
                            (el) => el.label === e.target.value
                          )[0];
                        }}
                        clearable
                        list={masterApprovalReasons.map((el) => el.label)}
                      />
                    </Grid>
                  );
                })}

                <Grid item xs={12}>
                  <Button
                    type="button"
                    color="inherit"
                    variant="outlined"
                    fullWidth
                    startIcon={<BCPSquarePlus />}
                    onClick={() => {
                      setApprovalReasons([...approvalReasons, {}]);
                    }}
                  >
                    Add Reason
                  </Button>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField
                  name="approvalReason"
                  label={
                    approvalStatus === "approve"
                      ? "Approval Notes"
                      : approvalStatus === "pending"
                      ? "Pending Notes"
                      : approvalStatus === "rejected"
                      ? "Rejection Notes"
                      : ""
                  }
                  value={approvalReason}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  onBlur={(e) => {
                    if (e.target.value !== approvalReason) {
                      setApprovalReason(e.target.value);
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value !== approvalReason) {
                      setApprovalReason(e.target.value);
                    }
                  }}
                  disabled={authContext?.authState?.identity?.isAdmin !== true}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={(e) => {
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color={
                  approvalStatus === "approve"
                    ? "success"
                    : approvalStatus === "pending"
                    ? "warning"
                    : "error"
                }
                fullWidth
                onClick={updateOrganization}
              >
                {organization?.approvalStatus === approvalStatus
                  ? approvalStatus === "approve"
                    ? "Update Approval"
                    : approvalStatus === "pending"
                    ? "Update Pending"
                    : approvalStatus === "rejected"
                    ? "Update Rejected"
                    : ""
                  : approvalStatus === "approve"
                  ? "Approve"
                  : approvalStatus === "pending"
                  ? "Pending"
                  : approvalStatus === "rejected"
                  ? "Reject"
                  : ""}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}

export async function approveOrganization(options) {
  const { organizationId, approverId } = options;
  var updatedOrg;
  try {
    updatedOrg = await postData(`/gamestores/${organizationId}/approval`, {
      body: {
        approverId: approverId,
        approvalStatus: "approved",
      },
    });
    postNotification("success", `Organization approved`);
  } catch (err) {
    postNotification("error", err);
  }
  return updatedOrg;
}

export function ApprovalButton(props) {
  const { organization, handleUpdates, dirty } = props;
  const confirm = useConfirm();
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  return (
    <>
      <Button
        fullWidth
        color="success"
        variant={
          organization.approvalStatus === "approved" ? "contained" : "outlined"
        }
        onClick={async (e) => {
          if (organization.approvalStatus === "approved") return;
          if (dirty) {
            postNotification(
              "info",
              "Please save the current record before approving."
            );
            return;
          }
          try {
            await confirm({
              title: `Are you sure?`,
              description:
                "Are you sure you want to approve this organization?",
              dialogProps: {
                sx: {
                  zIndex: 99999,
                },
              },
            });
          } catch (err) {
            return;
          }
          setLoading(true);
          var updates = await approveOrganization({
            organizationId: organization?.id,
            approverId: authContext?.authState?.identity?.userId,
          });
          if (handleUpdates) {
            handleUpdates(updates);
          }
          setLoading(false);
        }}
      >
        {organization.approvalStatus === "approved" ? "Approved" : "Approve"}
      </Button>
      {loading && <ModalLoadingFull />}
    </>
  );
}

export function RejectButton(props) {
  const { organization, handleUpdates, ...rest } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        fullWidth
        color="error"
        variant={
          organization.approvalStatus === "rejected" ? "contained" : "outlined"
        }
        onClick={(e) => {
          setOpen(true);
        }}
      >
        {organization.approvalStatus === "rejected" ? "Rejected" : "Reject"}
      </Button>
      {open && (
        <ApprovalModal
          {...rest}
          handleUpdates={handleUpdates}
          organization={organization}
          approvalStatus="rejected"
          open={open}
          setOpen={setOpen}
        />
      )}
    </>
  );
}

export function PendingButton(props) {
  const { organization, handleUpdates, ...rest } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        fullWidth
        color="warning"
        variant={
          organization.approvalStatus === "pending" ? "contained" : "outlined"
        }
        onClick={(e) => {
          setOpen(true);
        }}
      >
        Pending
      </Button>
      {open && (
        <ApprovalModal
          {...rest}
          handleUpdates={handleUpdates}
          organization={organization}
          approvalStatus="pending"
          open={open}
          setOpen={setOpen}
        />
      )}
    </>
  );
}
